<script lang="ts">
  import ShortcutTable from '../components/ShortcutTable.svelte';
  import { toasts } from '../components/toast-queue.svelte';
  import { theme } from '../components/theme-state.svelte';
  import {
    exportFileName,
    parseImport,
    serializeExport,
    toLocalDate,
    type ExportFile,
    type ProgressState,
  } from '../lib/progress';
  import { THEME_LABELS, THEME_PREFERENCES, type ThemePreference } from '../lib/theme';
  import ConfirmDialog from './drill/ConfirmDialog.svelte';
  import { noticeMessage, type ProgressView } from './drill/progress-view.svelte';

  /**
   * 設定（layout-and-navigation.md §7）。テーマと、端末のデータの書き出し・読み込み（progress-store.md §6）。
   * 読み込みは parseImport で先に検査し、確認ダイアログで件数を見せてから丸ごと置き換える。
   */
  interface Props {
    section?: 'data' | undefined;
    progress: ProgressView;
  }

  let { section, progress }: Props = $props();
  let fileInput: HTMLInputElement | undefined = $state();
  let importError = $state<string | null>(null);
  let pending = $state.raw<{ text: string; file: ExportFile } | null>(null);

  function selectTheme(pref: ThemePreference) {
    if (!theme.set(pref)) {
      toasts.show({
        message: 'この端末にテーマを保存できませんでした。今の画面には反映しています。',
        kind: 'warn',
      });
    }
  }

  function summarize(p: ProgressState): { cards: number; answers: number; updated: string | null } {
    const latest = p.history[0]?.at;
    return {
      cards: Object.keys(p.cards).length,
      answers: p.history.length,
      updated: latest === undefined ? null : toLocalDate(new Date(latest)),
    };
  }

  const current = $derived(summarize(progress.snapshot.progress));
  const incoming = $derived(pending ? summarize(pending.file.progress) : null);

  function exportClick() {
    const { file, notices } = progress.exportFile();
    const name = exportFileName(progress.now());
    const url = URL.createObjectURL(
      new Blob([serializeExport(file)], { type: 'application/json' }),
    );
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.hidden = true;
    document.body.append(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 0);
    for (const n of notices) toasts.show(noticeMessage(n));
    toasts.show({ message: `${name} を書き出しました。`, kind: 'ok' });
  }

  async function importChange(e: Event) {
    const input = e.currentTarget;
    if (!(input instanceof HTMLInputElement)) return;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;
    importError = null;
    let text: string;
    try {
      text = await file.text();
    } catch {
      importError = 'ファイルを読めませんでした。';
      return;
    }
    const parsed = parseImport(text);
    if (!parsed.ok) {
      importError = parsed.message;
      return;
    }
    pending = { text, file: parsed.file };
  }

  function undoImport() {
    const r = progress.undoImport();
    if (!r) return;
    for (const n of r.notices) toasts.show(noticeMessage(n));
    if (r.ok) toasts.show({ message: '読み込む前のデータに戻しました。', kind: 'ok' });
    else importError = r.message;
  }

  function confirmImport() {
    const target = pending;
    pending = null;
    if (!target) return;
    const r = progress.importText(target.text);
    for (const n of r.notices) toasts.show(noticeMessage(n));
    if (!r.ok) {
      importError = r.message;
      return;
    }
    toasts.show({
      message: 'データを読み込みました。',
      kind: 'ok',
      action: { label: '元に戻す', run: undoImport },
    });
  }

  $effect(() => {
    if (section === 'data') document.getElementById('data-title')?.scrollIntoView();
  });
</script>

<div class="page page-narrow">
  <div class="page-head">
    <h1 tabindex="-1">設定</h1>
  </div>

  <section class="panel" aria-labelledby="theme-title">
    <h2 id="theme-title">テーマ</h2>
    <fieldset class="radios" data-testid="theme">
      <legend class="visually-hidden">テーマ</legend>
      {#each THEME_PREFERENCES as pref (pref)}
        <label class="radio">
          <input
            type="radio"
            name="theme"
            value={pref}
            checked={theme.pref === pref}
            onchange={() => selectTheme(pref)}
            data-testid="theme-{pref}"
          />
          {THEME_LABELS[pref]}
        </label>
      {/each}
    </fieldset>
  </section>

  <section class="panel" aria-labelledby="data-title">
    <h2 id="data-title">データ</h2>
    <p class="text-soft small">
      データはこのブラウザの中だけに保存されます。別の端末へ移すときや控えを取るときは書き出してください。
    </p>
    <p class="small" data-testid="data-summary">
      回答 <span class="num">{current.answers}</span> 件
    </p>
    <div class="row">
      <button class="btn" type="button" data-testid="export" onclick={exportClick}>書き出す</button>
      <button
        class="btn"
        type="button"
        data-testid="import"
        onclick={() => {
          importError = null;
          fileInput?.click();
        }}
      >
        読み込む
      </button>
      {#if progress.hasBackup}
        <button class="btn" type="button" data-testid="undo-import" onclick={undoImport}>
          元に戻す
        </button>
      {/if}
      <input
        bind:this={fileInput}
        class="visually-hidden"
        type="file"
        accept="application/json,.json"
        tabindex="-1"
        aria-hidden="true"
        data-testid="import-file"
        onchange={importChange}
      />
    </div>
    {#if importError}
      <p class="import-error" role="alert" data-testid="import-error">
        読み込めませんでした。{importError}。
      </p>
    {/if}
  </section>

  <section class="panel" aria-labelledby="keys-title">
    <h2 id="keys-title">ショートカット</h2>
    <ShortcutTable />
  </section>
</div>

<ConfirmDialog
  open={pending !== null}
  title="データを置き換えますか"
  confirmLabel="置き換える"
  cancelLabel="やめる"
  testid="import-dialog"
  onconfirm={confirmImport}
  oncancel={() => (pending = null)}
>
  <p>
    今のデータをすべて、読み込むファイルの内容に置き換えます。置き換えた後でも「元に戻す」で戻せます。
  </p>
  {#if incoming}
    <table class="compare">
      <thead>
        <tr
          ><th scope="col"></th><th scope="col">今のデータ</th><th scope="col">読み込むデータ</th
          ></tr
        >
      </thead>
      <tbody>
        <tr>
          <th scope="row">カード</th>
          <td class="num">{current.cards} 枚</td>
          <td class="num">{incoming.cards} 枚</td>
        </tr>
        <tr>
          <th scope="row">回答</th>
          <td class="num">{current.answers} 件</td>
          <td class="num">{incoming.answers} 件</td>
        </tr>
        <tr>
          <th scope="row">最終更新</th>
          <td class="num">{current.updated ?? '—'}</td>
          <td class="num">{incoming.updated ?? '—'}</td>
        </tr>
      </tbody>
    </table>
  {/if}
</ConfirmDialog>

<style>
  .page-head {
    margin-bottom: var(--space-5);
  }
  .panel {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }
  .panel + .panel {
    margin-top: var(--space-5);
  }
  .small {
    font-size: var(--fs-sm);
  }
  .radios {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-2) var(--space-5);
    border: 0;
    padding: 0;
    margin: 0;
  }
  .radio {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    min-height: var(--tap-min);
    font-weight: var(--fw-regular);
    cursor: pointer;
  }
  .import-error {
    color: var(--err-text);
    font-size: var(--fs-sm);
  }
  .compare {
    border-collapse: collapse;
    font-size: var(--fs-sm);
  }
  .compare th,
  .compare td {
    padding: var(--space-1) var(--space-3);
    text-align: left;
  }
  .row {
    display: flex;
    gap: var(--space-3);
    flex-wrap: wrap;
  }
</style>
